import { RestCatalogAutocomplete } from "../schema-copy/api-schema";
import { normalizeForAutocomplete } from "../schema-copy/autocomplete-helper";
import { getAutocomplete } from "./api";
import { AuthData } from "./auth";

interface CacheEntry {
    normalizedTerm: string;
    result: RestCatalogAutocomplete;
}

const MAX_CACHE_SIZE = 50;
const cache: CacheEntry[] = [];
let lastNormalizedTerm = '';
let lastResult: RestCatalogAutocomplete | null = null;

function isComplete(result: RestCatalogAutocomplete): boolean {
    return result.length < 10;
}

function filterResults(result: RestCatalogAutocomplete, normalizedTerm: string): RestCatalogAutocomplete {
    return result.filter(([, title, domain]) => {
        const normalizedTitle = normalizeForAutocomplete(title);
        const normalizedDomain = normalizeForAutocomplete(domain);
        return normalizedTitle.startsWith(normalizedTerm) ||
               normalizedTitle == normalizedTerm ||
               normalizedDomain.startsWith(normalizedTerm) ||
               normalizedDomain == normalizedTerm;
    });
}

function addToCache(normalizedTerm: string, result: RestCatalogAutocomplete): void {
    // Remove existing entry if present
    const existingIndex = cache.findIndex(e => e.normalizedTerm == normalizedTerm);
    if (existingIndex >= 0) {
        cache.splice(existingIndex, 1);
    }

    // Add to front
    cache.unshift({ normalizedTerm, result });

    // Trim to max size
    if (cache.length > MAX_CACHE_SIZE) {
        cache.length = MAX_CACHE_SIZE;
    }
}

function findInCache(normalizedTerm: string): RestCatalogAutocomplete | null {
    // Check for exact match
    const exact = cache.find(e => e.normalizedTerm == normalizedTerm);
    if (exact) return exact.result;

    // Check for prefix match with complete results
    for (const entry of cache) {
        if (normalizedTerm.startsWith(entry.normalizedTerm) && isComplete(entry.result)) {
            return filterResults(entry.result, normalizedTerm);
        }
    }

    return null;
}

export async function autocomplete(auth: AuthData | undefined, term: string): Promise<RestCatalogAutocomplete | 'loading'> {
    const normalizedTerm = normalizeForAutocomplete(term);

    // Empty search terms return empty results
    if (!normalizedTerm) {
        return [];
    }

    // Check if this is an extension of the last search
    if (lastResult && normalizedTerm.startsWith(lastNormalizedTerm) && isComplete(lastResult)) {
        const filtered = filterResults(lastResult, normalizedTerm);
        lastNormalizedTerm = normalizedTerm;
        lastResult = filtered;
        return filtered;
    }

    // Check cache
    const cached = findInCache(normalizedTerm);
    if (cached) {
        lastNormalizedTerm = normalizedTerm;
        lastResult = cached;
        return cached;
    }

    // Indicate loading state before fetching
    return 'loading';
}

export async function autocompleteFetch(auth: AuthData | undefined, normalizedTerm: string): Promise<RestCatalogAutocomplete> {
    // Fetch from backend
    const result = await getAutocomplete(auth, normalizedTerm);

    // Update cache and last search
    addToCache(normalizedTerm, result);
    lastNormalizedTerm = normalizedTerm;
    lastResult = result;

    return result;
}
